import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import useTranslation from "../../customHooks/translations";
import TopBar from "../TopBar";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER} from "../../constants/theme";
import agent from "../../agent";
import {formatDateShort} from "../../utils/dateUtils";
import LockIcon from '@mui/icons-material/Lock';
import {connect} from "react-redux";
import FreakHubList from "./FreakHubList";
import CommonLoader from "../Loaders/CommonLoader";
import {isFreakHubAuthor, isFreakHubParticipantOrAuthor} from "../../utils/freakUtils";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditIcon from "@mui/icons-material/Edit";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {DELETE_FREAK_HUB} from "../../constants/actionTypes";
import {CHAT_ENABLED} from "../../constants/featureFlags";
import ConfirmationModal from "../ConfirmationModal";
import ApprovalStatusBadge from "../Moderation/ApprovalStatusBadge";

const FreakHubDetails = (props) => {
  const translation = useTranslation();
  const {slug} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const type = location?.state?.type || null;

  const [hubDetails, setHubDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [joinButtonMessage, setJoinButtonMessage] = useState(translation.freaks.operations.join);
  const [joinButtonDisabled, setJoinButtonDisabled] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLeaveModalOpen, setLeaveModalOpen] = useState(false);
  const [confirmMethod, setConfirmMethod] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const results = await agent.FreakHubs.get(slug);

      setHubDetails(results.freakHub);
      setSubscribed(results.freakHub.subscribed);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const handleRequestJoinClick = freakHub => {
    agent.FreakHubs.join(freakHub.id);
    setJoinButtonMessage(translation.freaks.operations.requested);
    setJoinButtonDisabled(true);
  };

  const handleOpenModal = (confirmMethod) => {
    setModalOpen(true);
    setConfirmMethod(confirmMethod);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleConfirm = () => {
    if (confirmMethod === 'delete') {
      props.onClickDelete(agent.FreakHubs.del(hubDetails.id));
    }
    handleCloseModal();
  };

  const isAuthor = hubDetails ? isFreakHubAuthor(props.currentUser?.id, hubDetails) : false;
  const isMember = hubDetails ? isFreakHubParticipantOrAuthor(props.currentUser?.id, hubDetails) : false;

  return (
    <>
      <TopBar/>
      {isLoading ? <CommonLoader/> : (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)', pb: 10 }}>
          <Box sx={{ maxWidth: 560, mx: 'auto', px: { xs: 1, sm: 2 } }}>

            {/* Hero banner */}
            <Box
              sx={{
                position: 'relative',
                borderRadius: '0 0 20px 20px',
                overflow: 'hidden',
                mb: 2,
                height: 200,
                background: hubDetails.backgroundImageUrl
                  ? `url("${hubDetails.backgroundImageUrl}") center/cover`
                  : 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
              <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Avatar
                  src={hubDetails?.imageUrl}
                  alt={hubDetails.name}
                  sx={{ width: 80, height: 80, borderRadius: 3, border: '3px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
                />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, textAlign: 'center', px: 2 }}>
                  {hubDetails.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {translation.hubDetails.createdAt}: {formatDateShort(hubDetails.createdAt)}
                </Typography>
                <ApprovalStatusBadge approvalStatus={hubDetails.approvalStatus} sx={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
                {!isMember && (
                  <Button
                    disabled={joinButtonDisabled}
                    variant="contained"
                    size="small"
                    sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, zIndex: 0, borderRadius: 2, px: 3, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
                    onClick={() => handleRequestJoinClick(hubDetails)}
                  >
                    {joinButtonMessage}
                  </Button>
                )}
              </Box>
            </Box>

            {/* Stats row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 3,
                py: 2,
                mb: 2,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>{hubDetails.freaksCount}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>{translation.hubDetails.freaksCount}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate(`/freak-hub/${hubDetails.slug}/users`)}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>{hubDetails.usersCount}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>{translation.hubDetails.usersCount}</Typography>
              </Box>
            </Box>

            {/* Description */}
            {hubDetails.description && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 2, px: 1, lineHeight: 1.6 }}>
                {hubDetails.description}
              </Typography>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
              {!isAuthor && (
                <IconButton
                  size="small"
                  sx={{ color: DEFAULT_THEME_COLOR }}
                  onClick={() => {
                    if (!subscribed) {
                      agent.Subscribe.createForFreakHub(hubDetails.id);
                      setSubscribed(true);
                    } else {
                      agent.Subscribe.deleteForFreakHub(hubDetails.id);
                      setSubscribed(false);
                    }
                  }}
                >
                  {subscribed
                    ? <NotificationsActiveIcon/>
                    : <NotificationsOffIcon/>
                  }
                </IconButton>
              )}
              {CHAT_ENABLED && isMember && hubDetails?.chatId && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MailOutlineIcon/>}
                  sx={{ color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR, zIndex: 0, borderRadius: 2, '&:hover': { borderColor: DEFAULT_THEME_COLOR, backgroundColor: 'rgba(54,201,162,0.08)' } }}
                  onClick={() => navigate('/messages', { state: { selectedGroupId: hubDetails?.chatId } })}
                >
                  Chat
                </Button>
              )}
              {isMember && hubDetails?.facebookMessengerGroupLink && (
                <Avatar
                  alt="Messenger"
                  src="/messenger-icon.png"
                  sx={{ width: 36, height: 36, cursor: 'pointer' }}
                  onClick={() => window.open(hubDetails.facebookMessengerGroupLink, '_blank')}
                />
              )}
              {isAuthor && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon/>}
                  sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, zIndex: 0, borderRadius: 2, '&:hover': { background: DEFAULT_GRADIENT_HOVER } }}
                  onClick={() => navigate(`/freak-hub-editor/${hubDetails.slug}`)}
                >
                  {translation.hubs.editMessage}
                </Button>
              )}
              {isAuthor && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon/>}
                  sx={{ color: '#f44336', borderColor: 'rgba(244,67,54,0.5)', zIndex: 0, borderRadius: 2, '&:hover': { borderColor: '#f44336', backgroundColor: 'rgba(244,67,54,0.08)' } }}
                  onClick={() => handleOpenModal('delete')}
                >
                  {translation.hubs.deleteMessage}
                </Button>
              )}
            </Box>

            {/* Freak list or locked message */}
            {isMember
              ? <Box sx={{ mb: 6 }}>
                  <FreakHubList tab={'forFreakHub'} context={props.context} forFreakHub={hubDetails.slug} />
                </Box>
              : <Box sx={{ minHeight: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Button startIcon={<LockIcon/>} variant="outlined" sx={{ color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR, zIndex: 0 }}>
                    {translation.hubs.privateView}
                  </Button>
                </Box>
            }
          </Box>
        </Box>
      )}
      <ConfirmationModal isOpen={isModalOpen} onClose={handleCloseModal} onConfirm={handleConfirm}/>
    </>
  )
};

const mapStateToProps = state => ({
  context: state.common.context,
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = dispatch => ({
  onClickDelete: payload =>
    dispatch({type: DELETE_FREAK_HUB, payload}),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakHubDetails);
